import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { qs } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { brandColor, basisLabel, fmt, pct, downloadCSV } from "@/lib/utils-msip";
import { AreaChart, Area, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download } from "lucide-react";

export default function RTODetail() {
  const { code } = useParams();
  const { filters } = useApp();
  const [data, setData] = useState(null);

  useEffect(() => {
    api
      .get(`/rtos/${encodeURIComponent(code)}?${qs({ brand: filters.brand, basis: filters.basis, period: filters.period })}`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null));
  }, [code, filters.brand, filters.basis, filters.period]);

  if (!data) return <div className="text-sm text-muted-foreground py-20 text-center">Loading…</div>;

  const brands = data.brands;
  const exportCsv = () =>
    downloadCSV(
      `rto_${code}_monthly.csv`,
      ["Month", "TIV", ...brands.map((b) => `${b} %`)],
      data.monthly.map((m) => [m.month, m.tiv, ...brands.map((b) => m[b])]),
      filters.basis
    );

  return (
    <div className="space-y-5" data-testid="rto-detail">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/rtos" className="text-muted-foreground hover:text-foreground" data-testid="back-to-explorer"><ArrowLeft size={18} /></Link>
        <h1 className="font-heading text-2xl font-extrabold tracking-tight">{data.geo.rto_code}</h1>
        <span className="text-xs rounded bg-secondary px-2 py-1">{data.geo.district}</span>
        {data.geo.in_incheon_territory && <span className="text-xs rounded bg-emerald-50 text-emerald-800 px-2 py-1">Incheon territory</span>}
        {data.target != null && <span className="text-xs rounded bg-secondary px-2 py-1">Target {pct(data.target)} ({basisLabel(filters.basis)})</span>}
        <Button variant="outline" size="sm" className="ml-auto" onClick={exportCsv} data-testid="detail-export-csv-btn">
          <Download size={14} className="mr-1" /> CSV
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="border bg-card rounded-md p-4" data-testid="brand-mix-chart">
          <h3 className="font-heading text-sm font-semibold mb-3">Brand mix over time ({basisLabel(filters.basis)})</h3>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={data.monthly} margin={{ top: 4, right: 12, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e9f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} unit="%" />
              <Tooltip formatter={(v) => `${v}%`} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {brands.map((b) => (
                <Area key={b} type="monotone" dataKey={b} stackId="1" stroke={brandColor(b)} fill={brandColor(b)} fillOpacity={0.75} />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="border bg-card rounded-md p-4" data-testid="daily-bars-chart">
          <h3 className="font-heading text-sm font-semibold mb-3">Registrations added per upload (deltas)</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.daily} margin={{ top: 4, right: 12, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e9f0" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip labelFormatter={(d, p) => { const row = p?.[0]?.payload; return row?.from_date ? `${d} (since ${row.from_date})` : `${d} (baseline)`; }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="total" name="Market" fill="#94a3b8" />
              <Bar dataKey="brand" name={filters.brand} fill={brandColor(filters.brand)} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="border bg-card rounded-md p-4" data-testid="brand-ranking">
          <h3 className="font-heading text-sm font-semibold mb-1">Brand ranking</h3>
          <p className="text-[11px] text-muted-foreground mb-2">{data.ranking_label} · TIV {fmt(data.ranking_tiv)}</p>
          <div className="divide-y">
            {(data.ranking || []).map((r) => (
              <div key={r.brand} data-testid={`ranking-row-${r.brand}`} className={`flex items-center gap-2 py-1.5 text-sm ${r.brand === filters.brand ? "bg-secondary/70 -mx-2 px-2 rounded" : ""}`}>
                <span className="num w-7 font-bold text-muted-foreground">#{r.rank}</span>
                <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: brandColor(r.brand) }} />
                <span className="font-medium">{r.brand}</span>
                <span className="ml-auto num text-muted-foreground">{fmt(r.units)}</span>
                <span className="num w-16 text-right font-semibold">{pct(r.share)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2 border bg-card rounded-md p-4" data-testid="rank-history-chart">
          <h3 className="font-heading text-sm font-semibold mb-3">{filters.brand} rank history</h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data.monthly} margin={{ top: 4, right: 12, left: -28, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e9f0" />
              <XAxis dataKey="month" tick={{ fontSize: 10 }} />
              <YAxis reversed allowDecimals={false} tick={{ fontSize: 11 }} domain={[1, 8]} />
              <Tooltip formatter={(v) => `#${v}`} />
              <Line type="monotone" dataKey="rank" stroke={brandColor(filters.brand)} strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="border bg-card rounded-md overflow-auto" data-testid="monthly-share-table">
        <table className="w-full text-sm">
          <thead className="border-b sticky top-0 bg-card">
            <tr>
              <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Month</th>
              <th className="px-3 py-2 text-right text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">TIV</th>
              {brands.map((b) => (
                <th key={b} className="px-3 py-2 text-right text-[11px] uppercase tracking-wider font-semibold" style={{ color: brandColor(b) }}>{b}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.monthly.map((m) => (
              <tr key={m.month} className="border-b last:border-0 hover:bg-muted/50">
                <td className="px-3 py-1.5 font-medium">{m.month}</td>
                <td className="px-3 py-1.5 text-right num">{fmt(m.tiv)}</td>
                {brands.map((b) => (
                  <td key={b} className="px-3 py-1.5 text-right num">{pct(m[b])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
